import _ from 'lodash'
import Heading from '@tiptap/extension-heading'
import Blockquote from '@tiptap/extension-blockquote'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { Table } from '@tiptap/extension-table'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableRow } from '@tiptap/extension-table-row'
import { StarterKit } from '@tiptap/starter-kit'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Editor } from '@tiptap/react'
import { EditorState, Transaction } from 'prosemirror-state'
import { common, createLowlight } from 'lowlight'
import { Markdown } from '@tiptap/markdown'
import { StyleType } from '../../../../utils/markdown'
import { ActiveCellHighlight } from './TableInteractions/activeCellHighlightExtension'

const lowlight = createLowlight(common)

export const getEditorExtensions = () => {
  return [
    StarterKit.configure({
      codeBlock: false,
      heading: false,
      blockquote: false,
      horizontalRule: false,
      paragraph: false,
    }),
    CustomParagraph,
    CustomHeading,
    CustomBlockquote,
    Link,
    Image.configure({
      inline: true,
    }),
    CustomCodeBlock.configure({
      lowlight,
    }),
    CustomHorizontalRule,
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Table,
    TableCell,
    TableHeader,
    TableRow,
    ActiveCellHighlight,
    Markdown.configure({
      markedOptions: {
        breaks: true,
      },
    }),
  ]
}

// Markdownソース編集用の最小構成。装飾系を無効化してプレーンテキスト扱いにする。
// @tiptap/markdown は含めず、editor.getText() で生マークダウン文字列として取り出す。
export const getMarkdownSourceExtensions = () => {
  return [
    StarterKit.configure({
      blockquote: false,
      bold: false,
      bulletList: false,
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      italic: false,
      link: false,
      listItem: false,
      listKeymap: false,
      orderedList: false,
      strike: false,
      underline: false,
    }),
  ]
}

// Markdown文字列を、Markdownソースエディタ用のdocノードに変換する。
// 改行ごとに段落を作ることで、getText({ blockSeparator: '\n' }) で元の文字列に戻せる。
export const markdownToSourceDoc = (markdown: string) => {
  const lines = markdown.split('\n')
  return {
    type: 'doc',
    content: lines.map(line =>
      line.length > 0
        ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
        : { type: 'paragraph' }
    ),
  }
}

// Markdownソースエディタから、生のMarkdown文字列を取り出す。
export const getMarkdownFromSourceEditor = (editor: Editor): string => {
  return editor.getText({ blockSeparator: '\n' })
}

const EMPTY_PARAGRAPH_MARKER = '\xA0'
const CustomParagraph = Paragraph.extend({
  renderMarkdown: (node, h, ctx) => {
    const content = Array.isArray(node.content) ? node.content : []
    if (content.length > 0) {
      return h.renderChildren(content)
    }
    const previousNode = ctx?.previousNode
    if (!previousNode) return ''
    const previousIsEmptyParagraph =
      previousNode.type === 'paragraph' &&
      (!Array.isArray(previousNode.content) ||
        previousNode.content.length === 0)
    const previousIsNonParagraph = previousNode.type !== 'paragraph'
    return previousIsEmptyParagraph || previousIsNonParagraph
      ? EMPTY_PARAGRAPH_MARKER
      : ''
  },
})

const CustomCodeBlock = CodeBlockLowlight.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-e': () => this.editor.commands.toggleCodeBlock(),
    }
  },
  renderHTML({ HTMLAttributes }) {
    return ['pre', ['pre', ['code', HTMLAttributes, 0]]]
  },
})

const CustomBlockquote = Blockquote.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-.': () => this.editor.commands.toggleBlockquote(),
    }
  },
})

const CustomHeading = Heading.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-1': () => this.editor.commands.toggleHeading({ level: 1 }),
      'Mod-Shift-2': () => this.editor.commands.toggleHeading({ level: 2 }),
      'Mod-Shift-3': () => this.editor.commands.toggleHeading({ level: 3 }),
      'Mod-Shift-4': () => this.editor.commands.toggleHeading({ level: 4 }),
      'Mod-Shift-5': () => this.editor.commands.toggleHeading({ level: 5 }),
      'Mod-Shift-6': () => this.editor.commands.toggleHeading({ level: 6 }),
    }
  },
})

const CustomHorizontalRule = HorizontalRule.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-h': () => this.editor.commands.setHorizontalRule(),
    }
  },
})

export const splitHardBreaks = ({
  tr,
  state,
}: {
  tr: Transaction
  state: EditorState
}): boolean => {
  const { from, to } = state.selection
  const hardBreakPositions: number[] = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'hardBreak') {
      hardBreakPositions.push(pos)
    }
  })

  for (let i = hardBreakPositions.length - 1; i >= 0; i--) {
    const pos = hardBreakPositions[i]
    tr.delete(pos, pos + 1)
    tr.split(pos)
  }

  return true
}

export const findHighLightItemsInRichText = (editor: Editor): string[] => {
  let items: string[] = []
  if (editor.isActive('heading', { level: 1 })) {
    items = [...items, StyleType.HEADER_ONE]
  }
  if (editor.isActive('heading', { level: 2 })) {
    items = [...items, StyleType.HEADER_TWO]
  }
  if (editor.isActive('heading', { level: 3 })) {
    items = [...items, StyleType.HEADER_THREE]
  }
  if (editor.isActive('heading', { level: 4 })) {
    items = [...items, StyleType.HEADER_FOUR]
  }
  if (editor.isActive('heading', { level: 5 })) {
    items = [...items, StyleType.HEADER_FIVE]
  }
  if (editor.isActive('heading', { level: 6 })) {
    items = [...items, StyleType.HEADER_SIX]
  }
  if (editor.isActive('bold')) items = [...items, StyleType.BOLD]
  if (editor.isActive('italic')) items = [...items, StyleType.ITALIC]
  if (editor.isActive('strike')) items = [...items, StyleType.STRIKETHROUGH]
  if (editor.isActive('code')) items = [...items, StyleType.CODE]
  if (editor.isActive('codeBlock')) items = [...items, StyleType.CODE_BLOCK]
  if (editor.isActive('blockquote')) items = [...items, StyleType.BLOCKQUOTE]
  if (editor.isActive('bulletList')) {
    items = [...items, StyleType.UNORDERED_LIST_ITEM]
  }
  if (editor.isActive('orderedList')) {
    items = [...items, StyleType.ORDERED_LIST_ITEM]
  }
  if (editor.isActive('horizontalRule')) {
    items = [...items, StyleType.HORIZONTAL_RULE]
  }
  if (editor.isActive('taskList')) items = [...items, StyleType.TASK_LIST]
  if (editor.isActive('table')) items = [...items, StyleType.TABLE]
  if (editor.isActive('image')) items = [...items, StyleType.FILE]
  return items
}

const CLIPBOARD_DATA_TYPE_HTML: string = 'text/html'
const HTML_ANCHOR_TAG_REGEX: string = '<a '

export const insertLinkFromClipboard = (
  clipboardData: DataTransfer,
  editor: Editor
): boolean => {
  if (
    !clipboardData ||
    clipboardData.types.indexOf(CLIPBOARD_DATA_TYPE_HTML) < 0
  ) {
    return false
  }

  const html: string = clipboardData.getData(CLIPBOARD_DATA_TYPE_HTML)
  if (
    _.isEmpty(html) ||
    !new RegExp(HTML_ANCHOR_TAG_REGEX).test(html.replaceAll('\r\n', ''))
  ) {
    return false
  }
  editor.commands.insertContent(html, {
    parseOptions: {
      preserveWhitespace: false,
    },
  })
  return true
}
